import { useEffect, useState } from "react";

import {
    DatePicker, Button, RadioButton, TextArea, TextField,
    Table, TableMetadata, Select,
    Typography, useTable, AutocompleteMultipleSelect, Label, InfoPanel, TableStateWithFilters, ColumnSortEnum, Checkbox
} from "@king-ict/eupisi-ui/components";
import { CodeBook, PagedResult } from "@king-ict/eupisi-ui/models";
import { hrDate, codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col, Container, Modal as BSModal, Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { 
    getRegionalCommitteeById, saveRegionalCommittee, 
    getInstitutionsByRegionalCommitteeId, getInstitutionsByCountyId,
    searchInstitutionRegionalCommittee,
    searchRegionalCommitteeEmployee, getRegionalCommitteeEmployeeOib
} from "../../api/Committee/RegionalCommitteeApi";
import { searchMunicipalities, searchTowns } from "../../api/Geography/GeographyApi";
import { useAppDispatch } from "../../app/store";
import { ContentArea, FormCard, ListTable, SearchCard, ToolbarEdit, ToolbarTop } from "../../components";
import { useAuthCounties, useDidUpdateEffect, useInput } from "../../hooks";
import { useAuth } from "../../hooks/auth/useAuth";
import { InstitutionCodeBook, ValidationFailure } from "../../models";
import { RegionalCommittee, CommitteeStatus, 
    CommitteeEmployee, OibEmployee, RegionalCommitteeInstitution,
    RegionalCommitteeInstitutionSearch, RegionalCommitteeInstitutionSort 
} from "../../models/Committee";
import { CodeBookPagedFilter, GrandParentCodeBookPagedFilter, HierarchicalCodeBook, HierarchicalCodeBookPagedFilter, Selectable } from "../../models/Shared";
import { useGetInstitutionListForUserQuery } from "../../queries/codeBookQuery";
import { regionalCommitteeListRoute } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import { FormatUtils, MsgUtils, TableUtils, ObjectUtils } from "../../utils";

export function RegionalCommitteeEdit() {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const { handleChange } = useInput();
    const dispatch = useAppDispatch();
    const auth = useAuth();
    const oibRegex = /^\d{11}$/;
    const authCounties = useAuthCounties();
    const now = new Date();
    const defaultTab: string = "members";

    const createDefaultItem = (): RegionalCommittee => {
        return {
            id: 0,
            countyId: authCounties && authCounties.length > 0 ? authCounties[0].id : 1,
            name: "",
            dateFrom: new Date((now.getFullYear() + 1), now.getMonth(), now.getDate()),
            dateTo: new Date((now.getFullYear() + 3), now.getMonth(), now.getDate()),
            statusId: CommitteeStatus.NotDefined,
            isSelected: false,
        };
    };
    const createDefaultInstitutionSearch = (): RegionalCommitteeInstitutionSearch => {
        return initialSearch;
    };
    const [item, setItem] = useState<RegionalCommittee>(createDefaultItem);
    const [employeeTableData, setEmployeeTableData] = useState<CommitteeEmployee[]>([]);
    const employeeTableMetadata: TableMetadata<CommitteeEmployee> = {
        rowKey: "id",
        columns: [
            {
                headerClassName: "col-4",
                columnKey: "expertCommissionRole",
                header: t("common.expertCommission.role"),
                accessor: (item: CommitteeEmployee) => {
                    return (<div>{item.committeeRole?.name}</div>)
                },
            },
            {
                headerClassName: "col-5",
                columnKey: "employee",
                header: t("common.expertCommission.employee"),
                accessor: (item: CommitteeEmployee) => {
                    return (<div>{getCommitteeEmployeeDisplayName(item)}</div>)
                },
            },
            {
                headerClassName: "col-1",
                columnKey: "expertCommissionPosition",
                header: t("common.expertCommission.position"),
                accessor: (item: CommitteeEmployee) => {
                    return (<div>{item.committeePosition?.name}</div>)
                },
            },
            {
                headerClassName: "col-1",
                columnKey: "president",
                header: t("common.expertCommission.president"),
                accessor: (emp: CommitteeEmployee) => {
                    return (
                        <div className="pt-2 ml-3">
                            <RadioButton
                                ariaLabel="item.president"
                                key={emp.committeeRole?.id}
                                name={"item.president"}
                                checked={emp.isPresident}
                                onChange={(e) => handlePresidentChange(emp.committeeRole.id, emp.committeePosition!.id)}
                                value={emp.committeeRole!.id}
                                disabled={!emp.employee || !isEditing}
                            />
                        </div>
                    )
                },
            },
            {
                headerClassName: "col-1",
                columnKey: "edit",
                header: t("common.edit"),
                accessor: (item: CommitteeEmployee) => {
                    return (<div className={"text-right"}>
                        <Button
                            size="sm"
                            outline={true}
                            onClick={() => handleDetailsClicked(item.committeeRole!.id, item.committeePosition!.id)}
                            disabled={!isEditing}
                        >
                            {t("common.edit")}
                        </Button>
                    </div>)
                }
            },
        ],
        rowToolTip: () => undefined
    };
    const [employeeTableProps] = useTable(
        {
            metadata: employeeTableMetadata,
            data: employeeTableData,
            pagination: false,
            paginationPage: undefined,
            paginationPageSize: undefined,
            totalItems: 1,
            defaultSort: {}
        }
    );
    const storageKey = 'RegionalCommitteeInstitutionSearchTableState';
    const initialSearch: RegionalCommitteeInstitutionSearch = {
        countyId: item.countyId ?? (authCounties && authCounties.length > 0 ? authCounties[0].id : 1),
        dateFrom: new Date((now.getFullYear() + 1), now.getMonth(), now.getDate()),
        dateTo: new Date((now.getFullYear() + 3), now.getMonth(), now.getDate()),
        regionalCommitteeId: 0,
    };
    const createInitialTableState = () => {
        return TableUtils.CreateInitialTableState<RegionalCommitteeInstitutionSearch>(
          storageKey,
          TableUtils.tableDefaultState(RegionalCommitteeInstitutionSort.Code, ColumnSortEnum.ASC)
        )
    };
    const defaultInstitutionTableData = TableUtils.tableDefaultData<RegionalCommitteeInstitution & Selectable>()
    const [institutionTableData, setInstitutionTableData] = useState<PagedResult<RegionalCommitteeInstitution & Selectable>>(defaultInstitutionTableData);
    const [initialInstitutionTableState] = useState<TableStateWithFilters<RegionalCommitteeInstitutionSearch>>(() => createInitialTableState());
    const institutionTableMetadata: TableMetadata<RegionalCommitteeInstitution & Selectable> = {
        rowKey: "id",
        columns: [
            {
                columnKey: "select",
                header: "",
                accessor: (item: RegionalCommitteeInstitution & Selectable) => {
                    return (
                        <div>
                            <Checkbox
                                label={""}
                                name={nameof<typeof item>("id")}
                                value={item.id}
                                checked={item.isSelected}
                                onChange={handleInstitutionCheckedChange}
                            />
                        </div>)
                },
            },
            {
                columnKey: "code",
                header: t("common.institutionList.programCode"),
                accessor: (item: RegionalCommitteeInstitution) => {
                    return (
                      <div>
                        {item.code}
                      </div>)
                },
                sortBy: RegionalCommitteeInstitutionSort.Code
            },
            {
                columnKey: "town",
                header: t("common.institutionList.townName"),
                accessor: (item: RegionalCommitteeInstitution) => {
                  return (
                    <div>
                      {item.town}
                    </div>)
                },
                sortBy: RegionalCommitteeInstitutionSort.Town
            },
            {
                columnKey: "institutionName",
                header: t("common.institutionList.institutionName"),
                accessor: (item: RegionalCommitteeInstitution) => {
                    return (
                      <div>
                        {item.name}
                      </div>)
                  },
                  sortBy: RegionalCommitteeInstitutionSort.InstitutionName
            },
            {
                columnKey: "regionalCommitteeName",
                header: t("common.expertCommission.regionalCommitteeName"),
                accessor: (rcInstitution: RegionalCommitteeInstitution) => {
                    return (
                      <div>
                        {item.institutionList?.some(v => v === rcInstitution.id) ? item.name : ""}
                      </div>)
                  },
                  sortBy: undefined
            },
        ],
        rowToolTip: () => undefined
    };
    const [institutionTableProps, institutionTableState, setInstitutionTableFilters] = useTable<RegionalCommitteeInstitutionSearch>(
        {
            metadata: institutionTableMetadata,
            data: institutionTableData!.results,
            pagination: true,
            paginationPage: initialInstitutionTableState.page,
            paginationPageSize: initialInstitutionTableState.pageSize as any,
            totalItems: institutionTableData!.rowCount,
            defaultSort:  { order: initialInstitutionTableState.order, sortBy: initialInstitutionTableState.sortBy },
        }
    );
    const [search, setSearch] = useState<RegionalCommitteeInstitutionSearch>(createDefaultInstitutionSearch);
    const [tableSearch, setTableSearch] = useState<RegionalCommitteeInstitutionSearch>(createDefaultInstitutionSearch);
    const [statuses] = useState<CodeBook[]>(MsgUtils.getEnumToCodeBook(CommitteeStatus, "CommitteeStatus"));
    const [errors, setErrors] = useState<ValidationFailure[]>()
    const [filteredEmployees, setFilteredEmployees] = useState<CodeBook[]>([]);
    const [editing, setEditing] = useState<CommitteeEmployee | null>(null);
    const [employee, setEmployee] = useState<CodeBook>();
    const [oibEmployee, setOibEmployee] = useState<OibEmployee>();
    const [showOibAlert, setShowOibAlert] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [tabKey, setTabKey] = useState<string>(defaultTab);
    const { data: institutions } = useGetInstitutionListForUserQuery();
    //const [institutions, setInstitutions] = useState<CodeBook[]>([]);
    const [filteredInstitutions, setFilteredInstitutions] = useState<CodeBook[]>([]);
    const [filteredMunicipalities, setFilteredMunicipalities] = useState<HierarchicalCodeBook[]>([]);
    const [filteredTowns, setFilteredTowns] = useState<HierarchicalCodeBook[]>([]);
    const [filterChanged, setFilterChanged] = useState<boolean>(false);
    const [clearFilter, setClearFilter] = useState<boolean>(true);
    const [selectAll, setSelectAll] = useState<boolean>(false);

    useEffect(() => {
        (async () => {
            if (!isNaN(+id)) {
                let result = (await getRegionalCommitteeById(+id));
                if (result) {
                    setItem(result);
                    setEmployeeTableData(result.employees!)
                }
                if (+id === 0) {
                    setIsEditing(true);
                }
            }
        })();
        initialSearch.regionalCommitteeId = +id;
        setClearFilter(true);
        const institutionSearch: RegionalCommitteeInstitutionSearch = {
            ...search,
            regionalCommitteeId: +id,
            dateFrom: item.dateFrom ?? new Date(),
            dateTo: item.dateTo ?? new Date(),
        };
        setSearch(institutionSearch);
        setInstitutionTableFilters(institutionSearch);
        if (+id === 0)
            dispatch(setIsEdited())
        else 
            dispatch(resetIsEdited());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        initialSearch.countyId = item.countyId;
        setClearFilter(true);
        const newSearch: RegionalCommitteeInstitutionSearch = {
            ...search,
            countyId: item.countyId,
        }
        setSearch(newSearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.countyId]);

    useEffect (() => {
        handleSearchClicked(null);
    }, [search.countyId]);

    useEffect(() => {
        if (clearFilter) {
            setClearFilter(false);
            setFilterChanged(false);
        }
        else
            setFilterChanged(true);
        TableUtils.RemoveSessionStorageItem(storageKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    useEffect(() => {
        (async () => {
            var request = TableUtils.GetSearchRequest<RegionalCommitteeInstitutionSearch>(tableSearch, institutionTableState);
            var itemList = await searchInstitutionRegionalCommittee(request);
            if (itemList !== null && itemList.results.length > 0) {
                const pagedResult: PagedResult<RegionalCommitteeInstitution & Selectable> = {
                    ...itemList,
                    results: ObjectUtils.makeSelectableArray(itemList.results),
                };
                setInstitutionTableData(pagedResult);
            }
            else {
                setInstitutionTableData(defaultInstitutionTableData);
            }
            // Any time we clear the Update entity, we don't have anything selected.
            setSelectAll(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionTableState]);

    // useEffect(() => {
    //     (async () => {
    //         if (item) {
    //             if (item.id !== 0) {
    //                 let result = await getInstitutionsByRegionalCommitteeId(item.id);
    //                 setInstitutions(result != undefined ? result : []);
    //             } else {
    //                 let result = await getInstitutionsByCountyId(item.countyId);
    //                 setInstitutions(result != undefined ? result : []);
    //             }
    //         }
    //     })();
    // }, [item.id, item.countyId])

    const handleDetailsClicked = (expertCommissionRoleId: number, positionId: number) => {
        var expertCommissionEmployee = employeeTableData.find(value => value.committeeRole.id === expertCommissionRoleId && value.committeePosition?.id === positionId);
        if (expertCommissionEmployee) {
            setEditing(expertCommissionEmployee);
            setEmployee(expertCommissionEmployee.employee);
            setOibEmployee(expertCommissionEmployee.oibEmployee);
        }
    };
    const handleDateFromChangeAny = (e: any) => {
        var dateFrom = new Date(e.target.value);
        const newSearch: RegionalCommitteeInstitutionSearch = {
            ...search,
            dateFrom: dateFrom,
        };
        setSearch(newSearch);
        const newItem: RegionalCommittee = {
            ...item,
            dateFrom: dateFrom,
        };
        setItem(newItem);
        dispatch(setIsEdited());
    };
    const handleDateToChangeAny = (e: any) => {
        var dateTo = new Date(e.target.value);
        const newSearch: RegionalCommitteeInstitutionSearch = {
            ...search,
            dateTo: dateTo,
        };
        setSearch(newSearch);
        const newItem: RegionalCommittee = {
            ...item,
            dateTo: dateTo,
        };
        setItem(newItem);
        dispatch(setIsEdited());
    };
    const handleEmployeeFilterChange = async (filter: string) => {
        const search: CodeBookPagedFilter = {
            pageSize: 100,
            page: 1,
            search: filter,
        };
        var result = await searchRegionalCommitteeEmployee(search);
        setFilteredEmployees(result.results);
    };
    const handleEmployeeChangeMultiselect = (e: any) => {
        const employeeList = e.target.value as CodeBook[];
        const employeeCodeBook = employeeList.length > 0 ? employeeList[0] : undefined;
        setEmployee(employeeCodeBook);
        if (employeeCodeBook) {
            setOibEmployee(undefined);
        }
    };
    const handlePresidentChange = (roleId: number, positionId: number) => {
        const employees = employeeTableData.map(val => {
            val.isPresident = val.committeeRole.id === roleId && val.committeePosition?.id === positionId;
            return val;
        });

        const newItem: RegionalCommittee = {
            ...item,
            employees: employees,
        };
        setItem(newItem);
        dispatch(setIsEdited());
    };
    const handleNoteChange = (e: any) => {
        const note = e.target.value;
        const newItem: RegionalCommittee = {
            ...item,
            note: note,
        };
        setItem(newItem);
        dispatch(setIsEdited());
    };
    const handleOibChanged = (e: any) => {
        const oib = e.target.value as string;
        const newOibEmployee: OibEmployee = {
            ...oibEmployee,
            oib: oib,
            lastName: undefined,
        };
        setOibEmployee(newOibEmployee);
        setShowOibAlert(null);
    };
    const handleInstitutionFilterChange = async (filter: string) => {
        var items = institutions?.filter(v => v.name.includes(filter));
        setFilteredInstitutions(items || []);
    };
    const handleInstitutionChange = (e: any) => {
        var items = e.target.value as CodeBook[];
        const newItem: RegionalCommittee = {
            ...item,
            institutionList: items?.map(v => v.id),
        };
        setItem(newItem);
        dispatch(setIsEdited());
    };
    const handleOibSystemClick = async () => {
        if (oibEmployee?.oib) {
            var result = (await getRegionalCommitteeEmployeeOib(oibEmployee.oib));
            if (result && result.item && result.item.oib) {
                const newOibEmployee: OibEmployee = {
                    ...oibEmployee,
                    firstName: result.item.firstName,
                    lastName: result.item.lastName,
                };
                setOibEmployee(newOibEmployee);
                setEmployee(undefined);
            } else {
                setShowOibAlert(t(MsgUtils.getOibErrorMessage(result.status)));
            }
        }
    };
    const getCommitteeEmployeeDisplayName = (ce: CommitteeEmployee) => {
        var name = undefined;
        if (ce.employee) {
            name = ce.employee.name;
        } else if (ce.oibEmployee) {
            name = `${ce.oibEmployee.oib}, ${ce.oibEmployee.lastName}, ${ce.oibEmployee.firstName}`;
        }
        return name;
    };

    const handleMunicipalityFilterChange = async (filter: string) => {
        const mSearch: HierarchicalCodeBookPagedFilter = {
            pageSize: 500,
            page: 1,
            ids: search.municipalityIds,
            parentId: [item.countyId],
            search: filter,
        };
        var result = await searchMunicipalities(mSearch);
        setFilteredMunicipalities(result.results);
    };
    const handleMunicipalityChange = (e: any) => {
        const municipalityList = e.target.value as HierarchicalCodeBook[];
        let townList = filteredTowns.filter(v => municipalityList.find(c => c.id === v.parentId) !== undefined);
        const newSearch: RegionalCommitteeInstitutionSearch = {
            ...search,
            municipalityIds: municipalityList?.map(v => v.id),
            townIds: townList?.map(v => v.id),
        };
        setSearch(newSearch);
    };
    const handleTownFilterChange = async (filter: string) => {
        const tSearch: GrandParentCodeBookPagedFilter = {
            pageSize: 500,
            page: 1,
            ids: search.townIds,
            parentId: search.municipalityIds,
            grandParentId: [item.countyId],
            search: filter,
        };
        var result = await searchTowns(tSearch);
        setFilteredTowns(result.results);
    };
    const handleTownChange = (e: any) => {
        const townList = e.target.value as HierarchicalCodeBook[];
        const newSearch: RegionalCommitteeInstitutionSearch = {
            ...search,
            townIds: townList?.map(v => v.id),
        };
        setSearch(newSearch);
    };
    const handleInstitutionNameChange = (e: any) => {
        const name = e.target.value;
        const newSearch: RegionalCommitteeInstitutionSearch = {
            ...search,
            name: name,
        };
        setSearch(newSearch);
    };
    const handleSearchClicked = (e: any) => {
        setTableSearch(search)
        setFilterChanged(false);
        setInstitutionTableFilters({ ...tableSearch });
    };
    const handleClearSearchClicked = () => {
        setClearFilter(true);
        setSearch(createDefaultInstitutionSearch);
        setTableSearch(createDefaultInstitutionSearch);
        setInstitutionTableFilters({ ...tableSearch });
    };
    const handleInstitutionCheckedChange = (e: any) => {
        const institutionId = Number.parseInt(e.target.value);
        const isChecked = e.target.checked as boolean;
        const institutionArr = institutionTableData.results.slice();
        if (institutionId) {
            var institutionCodeBook = institutionArr.find(val => val.id === institutionId);
            if (isChecked) {
                institutionCodeBook!.isSelected = true;
            } else {
                setSelectAll(false);
                institutionCodeBook!.isSelected = false;
            }
            const newTableData: PagedResult<RegionalCommitteeInstitution & Selectable> = {
                ...institutionTableData,
                results: institutionArr,
            };
            setInstitutionTableData(newTableData);
            setAssignmentButtons();
        }
    };
    const handleSelectAllCheckedChange = (e: any) => {
        const isChecked = !selectAll;
        setSelectAll(isChecked);
        const newResults = institutionTableData.results.map(val => { val.isSelected = isChecked; return val; });
        const newTableData: PagedResult<RegionalCommitteeInstitution & Selectable> = {
            ...institutionTableData,
            results: newResults,
        };
        setInstitutionTableData(newTableData);
        setAssignmentButtons();
    };
    const handleAssignmentClicked = (isAssigning: boolean) => {
        var institutionIdArr = institutionTableData.results.filter(v => v.isSelected).map(v => v.id);
        var existingIdArr = item.institutionList?.slice() ?? [];
        if (isAssigning) {
            var newIdArr = institutionIdArr.filter(i => !existingIdArr.some(v => v === i));
            existingIdArr.push(...newIdArr);
        } else {
            existingIdArr = existingIdArr.filter(e => !institutionIdArr.some(v => v === e));
        }
        const newItem: RegionalCommittee = {
            ...item,
            institutionList: existingIdArr,
        };
        setItem(newItem);
    };
    const setAssignmentButtons = () => {
        // setAddAssignmentEnabled(u.schoolCandidateList.length > 0
        //     && !tableData.results.some(v => u.schoolCandidateList.some(val => val === v.id) && v.institutionId)
        //     && u.institutionId !== undefined);
        // setClearAssignmentEnabled(u.schoolCandidateList.length > 0
        //     && tableData.results.some(v => u.schoolCandidateList.some(val => val === v.id) && v.institutionId));
    };

    return (
        <ContentArea>
            <ToolbarTop
                showAdd={"hide"}
                showBack={"show"}
                showEdit={isEditing ? "hide" : "show"}
                pageTitle={t("common.expertCommission.regionPageNameEdit")}
                onClickEdit={() => { setIsEditing(true); dispatch(setIsEdited());}}
                errors={errors}
            />

            <FormCard>
                <Row>
                    <Col md={2}>
                        <Typography
                            variant="base"
                            color="light"
                        >
                            {t("common.expertCommission.county")}
                        </Typography>
                    </Col>
                    <Col md={4}>
                        {(isEditing && id === "0") &&
                        (<Select
                            name={nameof<typeof item>("countyId")}
                            value={item.countyId}
                            onChange={handleChange(setItem)}
                            selectData={codeBookToSelectData(authCounties)}
                            plaintext={!isEditing || id !== "0"}
                        />)
                        }
                        {(!isEditing || id !== "0") &&
                        <Typography
                            variant="base"
                            color="light"
                        >
                            {authCounties.find(v => v.id === item.countyId)?.name}
                        </Typography>
                        }
                    </Col>
                    <Col md={2}>
                        <Typography
                            variant="base"
                            color="light"
                        >
                            {t("common.status")}
                        </Typography>
                    </Col>
                    <Col md={4}>
                        <Label color={FormatUtils.getCommitteeStatusColor(item.statusId)} className="text-center" outline={false} fullWidth={false}>
                            {statuses.find(v => v.id === item.statusId)?.name}
                        </Label>
                    </Col>
                </Row>
                <Row>
                    <Col md={2}>
                        <Typography
                            variant="base"
                            color="light"
                        >
                            {t("common.expertCommission.name")}
                        </Typography>
                    </Col>
                    <Col md={4}>
                        <TextField
                            name={nameof<typeof item>("name")}
                            value={item.name}
                            onChange={handleChange(setItem)}
                            errorText={errors?.find(e => e.propertyName === "Name")?.message}
                            plaintext={!isEditing}
                        />
                    </Col>
                    <Col md={2}>
                        <Typography
                            variant="base"
                            color="light"
                        >
                            {t("common.expertCommission.dateFromDateTo")}
                        </Typography>
                    </Col>
                    <Col md={2}>
                        <DatePicker
                            name={"expertCommission.dateFrom"}
                            onChange={handleDateFromChangeAny}
                            disabled={item.dateFrom && item.dateFrom < new Date()}
                            value={item.dateFrom}
                            minYear={(new Date()).getFullYear() - 4}
                            maxYear={(new Date()).getFullYear() + 5}
                            plaintext={!isEditing}
                            errorText={errors?.find(e => e.propertyName === "DateFrom")?.message}
                        />
                    </Col>
                    <Col md={2}>
                        <DatePicker
                            name={"expertCommission.dateTo"}
                            onChange={handleDateToChangeAny}
                            disabled={item.dateFrom && item.dateFrom < new Date()}
                            value={item.dateTo}
                            minYear={(new Date()).getFullYear() - 4}
                            maxYear={(new Date()).getFullYear() + 5}
                            plaintext={!isEditing}
                            errorText={errors?.find(e => e.propertyName === "DateTo")?.message}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <TextArea
                            ariaLabel="item.note"
                            label={t("common.expertCommission.note")}
                            name={"item.note"}
                            value={item.note}
                            onChange={handleNoteChange}
                            errorText={errors?.find(e => e.propertyName === "Note")?.message}
                            maxLength={5000}
                            disabled={!isEditing}
                        />
                    </Col>
                </Row>
            </FormCard>
            <Tabs
                id="controlled-tab-example"
                activeKey={tabKey ?? "basicInfo"}
                onSelect={(k) => setTabKey(k ?? defaultTab)}
            >
                <Tab title={t("common.expertCommission.tabs.memberList")}
                    eventKey={defaultTab}
                    className={errors?.some(e => e.propertyName.startsWith("Employees")) ? "text-danger" : undefined}
                >
                    <FormCard>
                        <Table {...employeeTableProps} />
                    </FormCard>
                </Tab>
                <Tab title={t("common.expertCommission.tabs.institutionList")}
                    eventKey="institutions" 
                    disabled={(+id) === 0}
                    className={errors?.some(e => e.propertyName.startsWith("InstitutionList")) ? "text-danger" : undefined}
                >
                    {((item.institutionList?.length ?? 0) > 0) &&
                    <ul>
                        {item.institutionList?.map(v => (
                            <li>
                                <Typography component="span">
                                    {institutions?.find(i => i.id === v)?.name}
                                </Typography>
                            </li>
                        ))}
                    </ul>
                    }
                    {((item.institutionList?.length ?? 0) === 0) &&
                    <InfoPanel
                        color="danger"
                        message={t("common.expertCommission.noInstitutionMessage")}
                        fullWidth={true}
                    />
                    }
                </Tab>
                <Tab title={t("common.expertCommission.tabs.institutionAdd")}
                    eventKey="addInstitutions" 
                    disabled={(+id) === 0 || !isEditing}
                >
                <SearchCard
                    onClickSearch={handleSearchClicked}
                    onClickClearSearch={handleClearSearchClicked}
                    isFilterChanged={filterChanged}
                    hideDivider={true}
                >
                    <Row >
                        <Col md={6}>
                            <AutocompleteMultipleSelect<HierarchicalCodeBook>
                            label={t("common.schoolCandidateModel.municipalityList")}
                            name={nameof<typeof search>("municipalityIds")}
                            value={filteredMunicipalities.filter(v => search.municipalityIds?.some(val => val === v.id))}
                            valueProp="id"
                            textProp="name"
                            onChange={handleMunicipalityChange}
                            selectOptions={filteredMunicipalities}
                            onFilterChange={handleMunicipalityFilterChange}
                            maximumSelectedItems={10}
                            />
                        </Col>
                        <Col md={6}>
                            <TextField
                                label={t("common.schoolCandidateModel.institutionList")}
                                name={nameof<typeof search>("name")}
                                value={search.name}
                                onChange={handleInstitutionNameChange}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <AutocompleteMultipleSelect<HierarchicalCodeBook>
                            label={t("common.schoolCandidateModel.townList")}
                            name={nameof<typeof search>("townIds")}
                            value={filteredTowns.filter(v => search.townIds?.some(val => val === v.id))}
                            valueProp="id"
                            textProp="name"
                            onChange={handleTownChange}
                            selectOptions={filteredTowns}
                            onFilterChange={handleTownFilterChange}
                            maximumSelectedItems={10}
                            />
                        </Col>
                    </Row>
                </SearchCard>
                <FormCard
                    cardTitle={t("common.schoolCandidateModel.schoolCandidateAssign")}
                >
                    <Row className="m-1 pb-3 pt-3 bg-light border">
                        <Col md={2} className="pt-4">
                            <Checkbox
                                label={t("common.selectAll")}
                                name={"selectAll"}
                                checked={selectAll}
                                onChange={handleSelectAllCheckedChange}
                            />
                        </Col>
                        <Col md={2} className="pt-4">
                            <Button onClick={() => handleAssignmentClicked(false)}
                                outline={true}
                                size="sm">
                                {t("common.schoolCandidateModel.clearAssignments")}
                            </Button>
                        </Col>
                        <Col md={2} className="pt-4">
                            <Button onClick={() => handleAssignmentClicked(true)}
                                size="sm">
                                {t("common.expertCommission.addAssignments")}
                            </Button>
                        </Col>
                    </Row>
                </FormCard>
                <FormCard>
                    <ListTable tableProps={institutionTableProps} />
                </FormCard>
                </Tab>
            </Tabs>

            <ToolbarEdit
                isEdited={isEditing}
                showCancel={"show"}
                showSave={isEditing ? "show" : "hide"}
                saveText={t("common.saveAndApprove")}
                handleSave={async () => saveRegionalCommittee(item)}
                redirectUrlSaveOK={(id) => regionalCommitteeListRoute}
                setSaveErrors={setErrors}
            />
            {/* CommitteeEmployee Modal */}
            <BSModal
                size="lg"
                backdrop="static"
                centered
                animation={false}
                show={editing != null}
                onConfirm={() => setEditing(null)}
                onClose={() => setEditing(null)}
                onHide={() => setEditing(null)}
            >
                <BSModal.Header>
                    {editing?.committeeRole!.name}
                </BSModal.Header>
                <BSModal.Body>
                    <Container>
                        {showOibAlert === null || <InfoPanel color="danger" message="" dismissible={false} fullWidth={true} >{showOibAlert}</InfoPanel>}
                        <Row>
                            <Col md={9}>
                                <AutocompleteMultipleSelect
                                    label={t("common.expertCommission.employee")}
                                    name={"employeeId"}
                                    value={employee?.id !== undefined ? [employee] : []}
                                    valueProp={"id"}
                                    textProp={"name"}
                                    onChange={handleEmployeeChangeMultiselect}
                                    selectOptions={filteredEmployees}
                                    onFilterChange={handleEmployeeFilterChange}
                                    maximumSelectedItems={1}
                                />
                            </Col>
                        </Row>
                        {editing?.committeeRole.checkOIB &&
                            (<>
                                <Row>
                                    <Col md={8}>
                                        <TextField
                                            label={t("common.schoolCandidateModel.oib")}
                                            value={oibEmployee?.oib}
                                            onChange={handleOibChanged}
                                            disabled={employee?.id !== undefined}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <br />
                                        <Button onClick={handleOibSystemClick}
                                            disabled={!(oibEmployee?.oib !== undefined && oibRegex.test(oibEmployee.oib))}
                                            size="sm">
                                            {t("common.schoolCandidateModel.connectOibSystem")}
                                        </Button>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <div>{oibEmployee?.lastName !== undefined ? `${oibEmployee?.oib}, ${oibEmployee?.lastName}, ${oibEmployee?.firstName}` : ""}</div>
                                    </Col>
                                </Row>
                            </>)
                        }
                    </Container>
                </BSModal.Body>
                <BSModal.Footer>
                    <Button onClick={() => {
                        setEmployee(undefined);
                        setOibEmployee(undefined);
                        setEditing(null);
                        setFilteredEmployees([]);
                        setShowOibAlert(null);
                    }}
                        size="sm"
                        outline={true}>
                        {t("common.quit")}
                    </Button>
                    <Button onClick={() => {
                        editing!.employee = employee && employee.id ? employee : undefined;
                        editing!.oibEmployee = oibEmployee && oibEmployee.lastName ? oibEmployee : undefined;
                        if (editing!.committeeRole.id === 20) {//default for lječnik
                            let presidentExist = false;
                            employeeTableData.forEach((el) => presidentExist = el.isPresident || presidentExist);
                            editing!.isPresident = !presidentExist;
                        }
                        setFilteredEmployees([]);
                        setEmployee(undefined);
                        setOibEmployee(undefined);
                        dispatch(setIsEdited());
                        setEditing(null);
                        setShowOibAlert(null);
                    }}
                        size="sm">
                        {t("common.save")}
                    </Button>
                </BSModal.Footer>
            </BSModal>
        </ContentArea>
    );
}